/**
 * @keymaker/templates — the starter library.
 *
 * Sixteen designs across four families of layout. Each is a `Template`: one `SceneDoc` in
 * normalised units plus optional per-family overrides. The library ships **no** overrides, and
 * that is the point — `fitRegion` reserves a band that adapts to every family, and the `@family`
 * frame token picks the right bezel — so adding a design costs one file rather than one file per
 * device.
 *
 * The templates are our own designs. Nothing here is derived from another product's template
 * gallery; the layout archetypes (caption above a device, text beside it, a full bleed, a glow)
 * are the vocabulary the whole category shares.
 */
import {
  DEFAULT_FAMILIES,
  familyByKey,
  resolveTemplate,
  TemplateLibrary,
  validateTemplate,
} from '@keymaker/schema';
import type { OutputFamily, SceneDoc, Template } from '@keymaker/schema';
import { captionTopTemplates } from './templates/caption-top';
import { sideAndBleedTemplates } from './templates/side-and-bleed';
import { centeredTemplates } from './templates/centered';
import { composedTemplates } from './templates/composed';

export const TEMPLATES: readonly Template[] = [
  ...captionTopTemplates,
  ...sideAndBleedTemplates,
  ...centeredTemplates,
  ...composedTemplates,
];

export const LIBRARY: TemplateLibrary = TemplateLibrary.parse({ templates: [...TEMPLATES] });

export function templateById(id: string): Template | undefined {
  return TEMPLATES.find((template) => template.id === id);
}

export function requireTemplate(id: string): Template {
  const found = templateById(id);
  if (!found) {
    throw new Error(`Unknown template "${id}". Known: ${TEMPLATES.map((t) => t.id).join(', ')}`);
  }
  return found;
}

/** Template ids grouped by category, for the picker UI. */
export function templatesByCategory(): Map<string, Template[]> {
  const grouped = new Map<string, Template[]>();
  for (const template of TEMPLATES) {
    const bucket = grouped.get(template.category);
    if (bucket) bucket.push(template);
    else grouped.set(template.category, [template]);
  }
  return grouped;
}

/** The tokens a template needs values for, so the UI can prompt before rendering. */
export function requiredTokens(template: Template): string[] {
  const found = new Set<string>();
  const pattern = /\{\{\s*([\w.[\]-]+)\s*\}\}/g;
  for (const layer of template.scene.layers) {
    if (layer.type !== 'text') continue;
    for (const match of layer.content.matchAll(pattern)) if (match[1]) found.add(match[1]);
  }
  return [...found];
}

/**
 * Resolve a template by **id** for one family, or throw with the reason.
 *
 * Keyed by id rather than by object because that is how every caller refers to a template: a
 * project stores a template id, the API takes an id, and an MCP tool takes an id. Exposed so the
 * API, the MCP server and the editor all resolve identically — if the browser and the server were
 * looking at different scenes, the "what you see is what exports" guarantee would be decorative.
 */
export function resolveForFamily(templateId: string, familyKey: string): SceneDoc {
  const template = requireTemplate(templateId);
  const family: OutputFamily | undefined = familyByKey(DEFAULT_FAMILIES, familyKey);
  if (!family) {
    throw new Error(
      `Unknown family "${familyKey}". Known: ${DEFAULT_FAMILIES.map((f) => f.key).join(', ')}`,
    );
  }
  return resolveTemplate(template, family);
}

/** Every problem that stops a template from rendering, across every family. */
export function validateAll(families: readonly OutputFamily[] = DEFAULT_FAMILIES): {
  templateId: string;
  problems: string[];
}[] {
  return TEMPLATES.map((template) => ({
    templateId: template.id,
    problems: validateTemplate(template, families),
  })).filter((entry) => entry.problems.length > 0);
}

export { resolveTemplate, validateTemplate };
export type { OutputFamily, SceneDoc, Template };
